import type { RefObject } from "react";
import { useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { showWarning, ShowSuccessFetch } from "src/utils/toast/toastUtil";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import {
	getSAL6030_R01AllCount,
	getSAL6030_R01DetailList,
	getSAL6030_R01GroupList,
	getSAL6030_R01HeaderList,
} from "./Api_SAL6030_R01";
import { SAL6030_R01HeaderDto } from "./Dto_SAL6030_R01";
import { useSAL6030_R01Store } from "./Store_SAL6030_R01";
import useUiuxStore from "src/store/useUiuxStore";

interface Props {
	headerGridRef: RefObject<AgGridReact<SAL6030_R01HeaderDto> | null>;
}

export const Content_SAL6030_R01 = ({ headerGridRef }: Props) => {
	const {
		SAL6030_R01Filter,
		setSAL6030_R01List,
		setSelectedSAL6030_R01List,
		setAllCount,
		setSAL6030_R01GroupList: setGroupList,
		setSAL6030_R01DetailList: setDetailList,
		setSelectedSAL6030_R01Group: setSelectedGroup,
		setSelectedSAL6030_R01Detail: setSelectedDetail,
		setScrollTop,
		setGroupScrollTop,
		setDetailScrollTop,
	} = useSAL6030_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { setMessage } = useUiuxStore();

	useFetchActionbarAuth("SAL6030_R01");

	const fetchSubData = useCallback(
		async (itemCd: string) => {
			setSelectedGroup(undefined);
			setSelectedDetail(undefined);
			setGroupScrollTop(null);
			setDetailScrollTop(null);

			const [groupRes, detailRes] = await Promise.all([
				getSAL6030_R01GroupList(itemCd),
				getSAL6030_R01DetailList(itemCd),
			]);
			setGroupList(groupRes.data.data || []);
			setDetailList(detailRes.data.data || []);
		},
		[setGroupList, setDetailList, setSelectedGroup, setSelectedDetail, setGroupScrollTop, setDetailScrollTop]
	);

	const fetchData = useCallback(async () => {
		setScrollTop(null);
		setGroupScrollTop(null);
		setDetailScrollTop(null);
		setLoadingSpinner(true);
		try {
			const [listRes, countRes] = await Promise.all([
				getSAL6030_R01HeaderList("search", SAL6030_R01Filter, 0),
				getSAL6030_R01AllCount(SAL6030_R01Filter),
			]);

			const list = listRes.data.data || [];
			setSAL6030_R01List(list);
			setAllCount(countRes.data.data ?? 0);

			if (list.length > 0) {
				gridRefSelectedRowHandler(headerGridRef, setSelectedSAL6030_R01List, () => true);
				await fetchSubData(list[0].prntItemcd);
				ShowSuccessFetch(
					setMessage,
					`조회 완료 (총 ${list.length}/${countRes.data.data ?? 0}건)`
				);
				setTimeout(() => setMessage(""), 5000);
			} else {
				showWarning("데이터가 없습니다.");
				setSelectedSAL6030_R01List(undefined);
				setSelectedGroup(undefined);
				setSelectedDetail(undefined);
				setGroupList([]);
				setDetailList([]);
			}
		} catch {
			showWarning("조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		SAL6030_R01Filter,
		setSAL6030_R01List,
		setSelectedSAL6030_R01List,
		setAllCount,
		setGroupList,
		setDetailList,
		setSelectedGroup,
		setSelectedDetail,
		fetchSubData,
		setLoadingSpinner,
		setMessage,
		headerGridRef,
		setScrollTop,
		setGroupScrollTop,
		setDetailScrollTop,
	]);

	const handleHeaderRowClick = useCallback(
		async (data: SAL6030_R01HeaderDto) => {
			if (!data.prntItemcd) return;
			try {
				await fetchSubData(data.prntItemcd);
			} catch {
				setSelectedGroup(undefined);
				setSelectedDetail(undefined);
				setGroupList([]);
				setDetailList([]);
			}
		},
		[fetchSubData, setGroupList, setDetailList, setSelectedGroup, setSelectedDetail]
	);

	return { fetchData, handleHeaderRowClick };
};
